/**
 * Scope'y OAuth Cloudflare.
 *
 * UWAGA: pełna lista scope'ów NIE jest nigdzie opublikowana. Jedynym źródłem
 * prawdy jest `GET https://api.cloudflare.com/client/v4/oauth/scopes`, które
 * wymaga tokena API. Do czasu wykonania Etapu 0 (patrz docs/etap-0-oauth-setup.md)
 * wszystkie wpisy są placeholderami.
 *
 * PODMIANA PO ETAPIE 0 TO ZMIANA WYŁĄCZNIE W TYM PLIKU — zamień `todo(...)`
 * na `scope('<id>')` z wartości pola `id` zwróconej przez API.
 *
 * Format ID: dot-delimited, np. `account.read`, `workers-platform.read`.
 * Styl Wranglera (`zone:read`) jest przez Cloudflare ODRZUCANY —
 * "Colon-delimited scopes are not accepted".
 */

declare const scopeBrand: unique symbol;

/** ID scope'a OAuth. Marka nominalna, żeby nie dało się wpisać byle stringa. */
export type ScopeId = string & { readonly [scopeBrand]: true };

const TODO_PREFIX = '__TODO__';

/** Format ID scope'a wg dokumentacji: człony rozdzielone kropką. */
const SCOPE_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*(?:\.[a-z0-9]+(?:-[a-z0-9]+)*)+$/;

/**
 * Scope'y protokolarne, których NIGDY nie wpisujemy ręcznie.
 * Cloudflare dodaje/usuwa je automatycznie na podstawie `grant_types`
 * i `response_types` klienta OAuth.
 */
const PROTOCOL_SCOPES = new Set(['offline_access', 'offline', 'openid']);

/** Placeholder do czasu Etapu 0. Trzyma nazwę grupy uprawnień jako wskazówkę. */
function todo(permissionGroupName: string): ScopeId {
  return `${TODO_PREFIX}${permissionGroupName}` as ScopeId;
}

/** Waliduje i oznacza prawdziwy ID scope'a zwrócony przez /oauth/scopes. */
export function scope(id: string): ScopeId {
  if (PROTOCOL_SCOPES.has(id)) {
    throw new Error(
      `Scope "${id}" jest dodawany automatycznie przez Cloudflare na podstawie grant_types. ` +
        'Nie wpisuj go ręcznie.',
    );
  }
  if (id.includes(':')) {
    throw new Error(
      `Scope "${id}" używa dwukropka (styl Wranglera). Cloudflare odrzuca scope'y ` +
        'rozdzielone dwukropkiem — użyj formatu z kropką, np. "account.read".',
    );
  }
  if (!SCOPE_ID_PATTERN.test(id)) {
    throw new Error(`Scope "${id}" nie pasuje do formatu ID scope'a Cloudflare.`);
  }
  return id as ScopeId;
}

/**
 * Rejestr scope'ów używanych przez katalog.
 * Wartości w nawiasach to nazwy grup uprawnień API Cloudflare — docs mówią
 * "OAuth scope names correspond to Cloudflare API token permission names",
 * więc to one są wskazówką, czego szukać w odpowiedzi /oauth/scopes.
 */
export const SCOPES = {
  zoneRead: todo('Zone Read'),
  zoneSettingsRead: todo('Zone Settings Read'),
  zoneSettingsWrite: todo('Zone Settings Write'),
  zoneWafRead: todo('Zone WAF Read'),
  zoneWafWrite: todo('Zone WAF Write'),
  dnsRead: todo('DNS Read'),
  accountRead: todo('Account Read'),
} as const satisfies Record<string, ScopeId>;

export type ScopeKey = keyof typeof SCOPES;

export function isResolved(id: ScopeId): boolean {
  return !id.startsWith(TODO_PREFIX);
}

/** Nazwy grup uprawnień, dla których wciąż nie znamy prawdziwego ID scope'a. */
export function unresolvedScopes(): readonly string[] {
  return Object.values(SCOPES)
    .filter((s) => !isResolved(s))
    .map((s) => s.slice(TODO_PREFIX.length));
}

export function allScopesResolved(): boolean {
  return unresolvedScopes().length === 0;
}

/**
 * Wołane przed startem przepływu OAuth (Etap 3). Do tego czasu health check
 * raportuje `scopesResolved: false`, ale nie blokuje pracy nad resztą systemu.
 */
export function assertScopesResolved(): void {
  const missing = unresolvedScopes();
  if (missing.length > 0) {
    throw new Error(
      "Scope'y OAuth nie zostały jeszcze uzupełnione. Wykonaj Etap 0 " +
        '(docs/etap-0-oauth-setup.md), a następnie uzupełnij packages/shared/src/catalog/scopes.ts. ' +
        `Brakuje: ${missing.join(', ')}.`,
    );
  }
}
